<template>
  <div class="promotion-progress">
    <template v-if="isConditionsVariant">
      <p v-if="descriptionTeaser" class="promotion-progress__teaser">
        {{ descriptionTeaser }}
      </p>

      <div class="promotion-progress__conditions-card">
        <div class="promotion-progress__conditions-body">
          <PromotionGrowthIcon :color="color" />

          <div class="promotion-progress__conditions-meta">
            <div class="promotion-progress__value-group">
              <div class="promotion-progress__amount">{{ formattedTarget }} $</div>
              <div class="promotion-progress__label">{{ $t("dealerPromotions.target") }}</div>
            </div>
            <p v-if="measurementUnitLabel" class="promotion-progress__conditions-note">
              {{ measurementUnitLabel }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div v-if="showLevels" class="promotion-progress__levels">
        <span v-if="levelLabel" class="promotion-progress__level">{{ levelLabel }}</span>
        <span v-if="nextLevelLabel" class="promotion-progress__next-level">{{
          nextLevelLabel
        }}</span>
      </div>

      <div class="promotion-progress__bar">
        <span class="promotion-progress__fill" :style="fillStyle" />
      </div>

      <div class="promotion-progress__values">
        <div class="promotion-progress__value-group">
          <div class="promotion-progress__amount">{{ formattedSum }} $</div>
          <div class="promotion-progress__label">{{ $t("dealerPromotions.yourPurchases") }}</div>
        </div>

        <div class="promotion-progress__value-group promotion-progress__value-group--right">
          <div class="promotion-progress__amount">{{ formattedTarget }} $</div>
          <div class="promotion-progress__label">{{ targetLabel }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import PromotionGrowthIcon from "@/views/dealer-promotions/_components/PromotionGrowthIcon.vue";

export default {
  name: "PromotionProgress",

  components: {
    PromotionGrowthIcon,
  },

  props: {
    sum: {
      type: Number,
      default: 0,
    },

    targetValue: {
      type: Number,
      default: 0,
    },

    level: {
      type: Number,
      default: null,
    },

    nextLevel: {
      type: Number,
      default: null,
    },

    color: {
      type: String,
      default: "#3b82f6",
    },

    variant: {
      type: String,
      default: "dealer",
      validator: (value) => ["dealer", "conditions"].includes(value),
    },

    descriptionTeaser: {
      type: String,
      default: "",
    },

    measurementUnit: {
      type: String,
      default: "",
    },
  },

  computed: {
    isConditionsVariant() {
      return this.variant === "conditions";
    },

    measurementUnitLabel() {
      if (!this.measurementUnit) {
        return "";
      }

      const key = `promotions.measurementUnits.${this.measurementUnit}`;

      if (this.$te(key)) {
        return this.$t(key);
      }

      return "";
    },

    normalizedPercent() {
      if (!this.targetValue) {
        return 0;
      }

      return Math.min(100, (this.sum / this.targetValue) * 100);
    },

    formattedSum() {
      return this.formatAmount(this.sum);
    },

    formattedTarget() {
      return this.formatAmount(this.targetValue);
    },

    levelLabel() {
      if (!this.level) {
        return "";
      }

      return this.$t("dealerPromotions.level", { level: this.level });
    },

    nextLevelLabel() {
      if (!this.nextLevel) {
        return "";
      }

      return this.$t("dealerPromotions.nextLevel", { level: this.nextLevel });
    },

    showLevels() {
      return this.levelLabel || this.nextLevelLabel;
    },

    targetLabel() {
      if (!this.nextLevel) {
        return this.$t("dealerPromotions.target");
      }

      return this.$t("dealerPromotions.levelTarget", { level: this.nextLevel });
    },

    fillStyle() {
      return {
        width: `${this.normalizedPercent}%`,
        backgroundColor: this.color,
      };
    },
  },

  methods: {
    formatAmount(value) {
      return new Intl.NumberFormat("uk-UA", {
        maximumFractionDigits: 0,
      }).format(value || 0);
    },
  },
};
</script>

<style scoped lang="postcss">
.promotion-progress {
  @apply space-y-2;
}

.promotion-progress__levels {
  @apply flex items-center justify-between gap-2 text-xs;
}

.promotion-progress__level {
  @apply shrink-0 font-bold text-[#1e204d];
}

.promotion-progress__next-level {
  @apply ml-auto text-right text-[#5c6378];
}

.promotion-progress__bar {
  @apply h-2.5 w-full overflow-hidden rounded-full bg-gray-100;
}

.promotion-progress__fill {
  @apply block h-full rounded-full transition-all duration-300;
}

.promotion-progress__values {
  @apply flex items-start justify-between gap-4 pt-0.5;
}

.promotion-progress__value-group {
  @apply min-w-0;
}

.promotion-progress__value-group--right {
  @apply text-right;
}

.promotion-progress__amount {
  @apply text-base font-bold leading-tight text-[#1e204d];
}

.promotion-progress__label {
  @apply mt-1 text-xs text-[#5c6378];
}

.promotion-progress__teaser {
  @apply line-clamp-1 text-xs leading-relaxed text-[#5c6378];
}

.promotion-progress__conditions-card {
  @apply rounded-lg bg-gray-50 p-3;
}

.promotion-progress__conditions-body {
  @apply flex items-center gap-3;
}

.promotion-progress__conditions-meta {
  @apply flex min-w-0 flex-1 flex-col gap-1;
}

.promotion-progress__conditions-note {
  @apply text-2xs leading-snug text-[#5c6378];
}
</style>
